import { session } from '@/components/helpers/session'
import { ID } from '@omneo/omneo-sdk'
import { OmneoPortal } from './portal'

/**
 * Shared only while the request is in flight. Around eight components call useOmneoClient on a
 * page, and on failure nothing is written to session storage, so each one would otherwise repeat
 * the same auth request. Deliberately NOT caching the resolved OmneoClient: eleven call sites
 * still run profile.Connection() on whatever client they are handed, which rewrites that client's
 * endpoint, so one shared instance would let any of them corrupt every consumer at once.
 */
let inFlightIdToken = null

export class OmneoClient {
  id
  client
  portal

  static async fetchIdToken() {
    if (inFlightIdToken) return inFlightIdToken
    inFlightIdToken = OmneoClient.requestIdToken().finally(() => {
      inFlightIdToken = null
    })
    return inFlightIdToken
  }

  static async requestIdToken() {
    const customerId = GM_STATE?.customer?.id
    const customerSignature = GM_STATE?.customer?.signature

    if (!customerId || !customerSignature) return undefined

    const response = await fetch(
      `https://extension-shopify-v5r64nnrcq-ts.a.run.app/${GM_STATE.omneo.tenantId}/api/v1/auth/token`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: customerId,
          signature: customerSignature
        })
      }
    )

    const data = await response.json()
    const { token, exp: expiry } = data

    if (!token || !expiry) return undefined

    session.omneoAuthToken.set(token)
    session.omneoAuthExpiry.set(expiry)

    return {
      token,
      expiry
    }
  }

  static async init() {
    const customerId = GM_STATE?.customer?.id
    const isLoggedIn = GM_STATE?.customer?.logged

    const portal = new OmneoPortal(GM_STATE.omneo.tenantId)

    if (!isLoggedIn || !customerId) return new this(portal)

    const sessionToken = session.omneoAuthToken.get()
    const sessionExpiry = session.omneoAuthExpiry.get()
    if (sessionToken && sessionExpiry) {
      const id = new ID({
        tenant: GM_STATE.omneo.tenantId,
        IDToken: sessionToken,
        IDTokenExpiry: sessionExpiry
      })

      if (!id.auth.isTokenExpired()) {
        return new this(portal, id)
      }
    }

    const credentials = await OmneoClient.fetchIdToken()
    if (!credentials) {
      // fetchIdToken returns undefined when the customer signature is missing or the auth
      // endpoint returns no token. Destructuring it threw an opaque TypeError instead.
      throw new Error('Omneo sign-in failed: no token returned for this customer')
    }
    const { token: fetchedToken, expiry: fetchedExpiry } = credentials
    const id = new ID({
      tenant: GM_STATE.omneo.tenantId,
      IDToken: fetchedToken,
      IDTokenExpiry: fetchedExpiry
    })

    return new this(portal, id)
  }

  static async logout() {
    return new Promise((resolve) => {
      // Create invisible iframe
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = GM_STATE.omneo.apiBaseUrl ?? 'https://my.shoesandsox.com.au/'
      window.addEventListener('message', (e) => {
        if (e.data?.message === 'logout') {
          resolve()
        }
      })
      // Wait for iframe to load before sending message
      iframe.onload = () => {
        iframe.contentWindow.postMessage(
          {
            action: 'logout'
          },
          '*'
        )

        // Fallback to timeout if message is not received
        setTimeout(() => {
          resolve()
        }, 10000)
      }
      document.body.appendChild(iframe)
    })
  }

  constructor(portal, id) {
    this.id = id ?? undefined
    this.portal = portal
  }
}
